import React, {useCallback, useEffect, useRef, useState} from "react";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import instance from "../../api/axios";
import {useCart} from "../../context/CartContext";
import "../../scss/pots.scss";

// Product codes shown in the pots block on Phalaenopsis plant pages.
// The order here is the order on the page. Codes that no longer exist are
// skipped silently, so a deleted product cannot empty the whole block.
const POT_PRODUCT_CODES = [
    "407001083010", // Orchid KRS planter          2500
    "407001083013", // Orchid KRS planter          3300
    "407001083012", // Orchid KRS planter          1900
    "407001083019", // Orchid Planter              1200
    "407001083018", // Orchid Planter              2500
    "407001083017", // Orchid Planter               900
    "201000041009", // Orchid potting mix 2.5 l    1800
    "401010033013", // Orchid planter              1500
    "410000021001", // Orchid support, 1 pcs        500
    "407010009002", // Plastic vase                2800
    "401011083017", // Planter                     3500
    "401010009115", // Transparent orchid planter   700
    "401011083013", // Planter Lantana             3500
];

// Codes supplied for this block that no product currently answers to - the API
// returns 404 for each. Kept here so they are not lost: check them in the admin
// (deleted? disabled? mistyped?) and move them into the list above once fixed.
//   407000005001  201001043027  201001043017  201001046018
//   201001043028  401011054084  401011054083  201001046012
//   901000000168  40700000500   407001083005  407001083021

// The block belongs on the Phalaenopsis plants only. Matching on the name
// alone would also catch "Phalaenopsis Planter", which is itself a pot, so
// the product must additionally sit in the plants branch of the catalogue.
// language -> resolved products, filled on the first Phalaenopsis page opened.
const cache = new Map();

const isPhalaenopsisPlant = (product) => {
    const name = (product?.name || "").toLowerCase();
    const catalogs = (product?.catalogs || "").toLowerCase();
    return name.includes("phalaenopsis") && catalogs.includes("plants & seeds");
};

const productPath = (item, language) => {
    const category = (item?.catalogs || "")
        .replace(/^_/, "")
        .replace(/\s+/g, "-")
        .toLowerCase();
    return `/catalog/${category}/${item?.productCode}/${language}`;
};

const PhalaenopsisPots = ({product}) => {
    const {t, i18n} = useTranslation();
    const {addProduct} = useCart();
    const [pots, setPots] = useState([]);
    const rowRef = useRef(null);
    const [hasPrev, setHasPrev] = useState(false);
    const [hasNext, setHasNext] = useState(false);

    const show = isPhalaenopsisPlant(product);

    // Which way is there still something to scroll to. Drives the arrows, which
    // are the only hint that the row scrolls at all: macOS keeps the scrollbar
    // hidden until it is in use, so the clipped last tile reads as a bug.
    const updateArrows = useCallback(() => {
        const el = rowRef.current;
        if (!el) return;
        setHasPrev(el.scrollLeft > 1);
        setHasNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    }, []);

    useEffect(() => {
        updateArrows();
        window.addEventListener("resize", updateArrows);
        return () => window.removeEventListener("resize", updateArrows);
    }, [pots, updateArrows]);

    const scrollRow = (direction) => {
        const el = rowRef.current;
        if (!el) return;
        el.scrollBy({left: direction * Math.max(el.clientWidth * 0.8, 136), behavior: "smooth"});
    };

    useEffect(() => {
        if (!show || POT_PRODUCT_CODES.length === 0) {
            setPots([]);
            return;
        }

        const lang = i18n.language.slice(0, 2);

        // Same thirteen products on every Phalaenopsis page, so hold them for
        // the rest of the visit instead of refetching on each one.
        if (cache.has(lang)) {
            setPots(cache.get(lang));
            return;
        }

        let cancelled = false;
        const url = process.env.REACT_APP_BASE_URL;

        // One request for the whole list. Asking per code meant thirteen round
        // trips whose database work did not overlap, which took seconds - long
        // enough that the block visibly popped in after the rest of the page.
        instance
            .get(`${url}/abio/public/products/by-codes`, {
                params: {codes: POT_PRODUCT_CODES.join(","), language: lang},
            })
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                cache.set(lang, list);
                if (!cancelled) setPots(list);
            })
            .catch(() => {
                if (!cancelled) setPots([]);
            });

        return () => {
            cancelled = true;
        };
    }, [show, i18n.language]);

    if (!show || pots.length === 0) return null;

    return (
        <div className="pots">
            <div className="pots__head">
                <p className="pots__title">{t("item.pots")}</p>
                <div className="pots__arrows">
                    <button type="button" className="pots__nav" aria-label="<"
                            disabled={!hasPrev} onClick={() => scrollRow(-1)}>&#8249;</button>
                    <button type="button" className="pots__nav" aria-label=">"
                            disabled={!hasNext} onClick={() => scrollRow(1)}>&#8250;</button>
                </div>
            </div>
            <div className="pots__row" ref={rowRef} onScroll={updateArrows}>
                {pots.map((item) => {
                    const discounted = item?.discount > 0;
                    const path = productPath(item, i18n.language);
                    return (
                        <div className="pots__card" key={item?.productCode}>
                            <div className="pots__thumb">
                                <Link to={path}>
                                    <img src={item?.images?.[0]?.url}
                                         alt={item?.images?.[0]?.[`alt_${i18n.language}`] || item?.name}/>
                                </Link>
                                <button type="button"
                                        className="pots__add"
                                        title={t("item.add")}
                                        aria-label={t("item.add")}
                                        onClick={() => addProduct(item.productCode)}>+
                                </button>
                            </div>
                            <Link className="pots__meta" to={path}>
                                <p className="pots__price">
                                    {(discounted ? item?.discountPrice : item?.price)?.toLocaleString()} AMD
                                    {discounted &&
                                        <span className="pots__old">{item?.price?.toLocaleString()}</span>}
                                </p>
                                <p className="pots__name">{item?.name}</p>
                            </Link>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export {PhalaenopsisPots};
